import React, { useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

interface HCLPreviewPanelProps {
  hcl: string;
  filename?: string;
}

function highlightHCL(code: string): React.ReactNode[] {
  const lines = code.split('\n');
  return lines.map((line, i) => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;

    // Comments
    if (remaining.trimStart().startsWith('#') || remaining.trimStart().startsWith('//')) {
      return <div key={i}><span style={{ color: '#6a9955' }}>{line}</span></div>;
    }

    // Process tokens
    const tokenRegex = /(resource|module|variable|output|locals|terraform|provider|data|for_each|count|depends_on|lifecycle|dynamic|content)\b|("(?:[^"\\]|\\.)*")|(\b\d+\b)|(true|false|null)\b|(#.*$)/g;
    let lastIndex = 0;
    let match;

    while ((match = tokenRegex.exec(remaining)) !== null) {
      // Text before match
      if (match.index > lastIndex) {
        parts.push(<span key={key++}>{remaining.slice(lastIndex, match.index)}</span>);
      }

      if (match[1]) {
        // Keywords
        parts.push(<span key={key++} style={{ color: '#569cd6' }}>{match[0]}</span>);
      } else if (match[2]) {
        // Strings
        parts.push(<span key={key++} style={{ color: '#ce9178' }}>{match[0]}</span>);
      } else if (match[3]) {
        // Numbers
        parts.push(<span key={key++} style={{ color: '#b5cea8' }}>{match[0]}</span>);
      } else if (match[4]) {
        // Booleans/null
        parts.push(<span key={key++} style={{ color: '#569cd6' }}>{match[0]}</span>);
      } else if (match[5]) {
        // Comments
        parts.push(<span key={key++} style={{ color: '#6a9955' }}>{match[0]}</span>);
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < remaining.length) {
      parts.push(<span key={key++}>{remaining.slice(lastIndex)}</span>);
    }

    return <div key={i}>{parts.length > 0 ? parts : ' '}</div>;
  });
}

export const HCLPreviewPanel: React.FC<HCLPreviewPanelProps> = ({ hcl, filename = 'main.tf' }) => {
  const highlighted = useMemo(() => highlightHCL(hcl), [hcl]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(hcl);
  };

  const handleDownload = () => {
    const blob = new Blob([hcl], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!hcl) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          Add resources or modules to see the generated HCL preview
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, display: 'flex', gap: 0.5 }}>
        <Tooltip title="Copy to clipboard">
          <IconButton size="small" onClick={handleCopy} sx={{ color: 'grey.400' }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton size="small" onClick={handleDownload} sx={{ color: 'grey.400' }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          bgcolor: '#1e1e1e',
          color: '#d4d4d4',
          p: 2,
          borderRadius: 1,
          fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          overflow: 'auto',
          maxHeight: 600,
          '& div': { minHeight: '1.2em' },
        }}
      >
        {highlighted}
      </Box>
    </Box>
  );
};
